import React, { useState } from 'react';
import { Users, Clock, Award, Target, BookOpen, Star, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from '../mock';
import TrainingBooking from '../components/TrainingBooking';

const Training = () => {
  const [selectedProgram, setSelectedProgram] = useState(null);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1740914994068-cf0325e0ed36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtZWNoYW5pYyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NTgyMTkxOTN8MA&ixlib=rb-4.1.0&q=85" 
            alt="Professional automotive training facility"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/90"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
              Professional
              <span className="text-teal-600"> Training</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {mockData.training.description}
            </p>
            <div className="flex items-center justify-center space-x-8 pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-600">100+</div>
                <div className="text-gray-500 text-sm">Students Trained</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-600">25+</div>
                <div className="text-gray-500 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-600">90%</div>
                <div className="text-gray-500 text-sm">Success Rate</div>
              </div>
            </div>
            
            {/* Quick Booking CTA */}
            <div className="pt-6">
              <a
                href="#booking"
                className="bg-teal-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 inline-flex items-center space-x-2"
              >
                <BookOpen size={20} />
                <span>Book Training Now</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Training Programs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">
              Our Training
              <span className="text-teal-600"> Programs</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive courses designed to make you an automotive detailing expert
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.training.programs.map((program) => (
              <div 
                key={program.id} 
                className="bg-slate-50 rounded-xl p-8 border border-gray-200 hover:border-teal-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-lg cursor-pointer"
                onClick={() => setSelectedProgram(program)}
              >
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="w-16 h-16 bg-teal-600 rounded-lg flex items-center justify-center">
                      <BookOpen className="text-white" size={32} />
                    </div>
                    <div className="text-right space-y-1">
                      <div className="text-teal-600 font-bold text-xl">{program.price}</div>
                      <div className="text-gray-500 text-sm">{program.duration}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-2xl font-bold text-gray-900">{program.name}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{program.description}</p>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-gray-900">Course Modules</h4>
                    <div className="space-y-2">
                      {program.modules.map((module, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Check className="text-teal-600 flex-shrink-0" size={16} />
                          <span className="text-gray-500 text-sm">{module}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button className="w-full bg-teal-50 border border-teal-200 text-teal-600 py-3 rounded-lg font-semibold hover:bg-teal-100 transition-all duration-200 flex items-center justify-center space-x-2">
                    <span>View Details</span>
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Book Button */}
          <div className="text-center mt-12">
            <a
              href="#booking"
              className="inline-flex items-center space-x-2 bg-teal-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <BookOpen size={20} />
              <span>Book Any Training Program</span>
            </a>
          </div>
        </div>
      </section>

      {/* Program Details Modal */}
      {selectedProgram && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                    <BookOpen className="text-white" size={24} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedProgram.name}</h2>
                    <p className="text-teal-600 font-semibold">{selectedProgram.duration}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedProgram(null)}
                  className="text-gray-500 hover:text-gray-700 transition-colors p-2 text-2xl"
                >
                  ×
                </button>
              </div>

              <p className="text-gray-600 text-lg leading-relaxed">{selectedProgram.description}</p>

              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Course Modules</h3>
                <div className="grid grid-cols-1 gap-3">
                  {selectedProgram.modules.map((module, index) => (
                    <div key={index} className="flex items-center space-x-3 bg-slate-50 p-4 rounded-lg">
                      <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">{index + 1}</span>
                      </div>
                      <span className="text-gray-700">{module}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200 space-y-4">
                <a
                  href="#booking"
                  className="w-full bg-teal-600 text-white py-4 rounded-lg font-bold text-center block hover:bg-teal-700 transition-all duration-200"
                  onClick={() => setSelectedProgram(null)}
                >
                  Book This Program
                </a>
                <p className="text-center text-gray-500 text-sm">
                  Contact us for detailed curriculum and enrollment information
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Training Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">
              Training
              <span className="text-teal-600"> Benefits</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Why choose Unisol Global for your professional development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.training.benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star className="text-white" size={24} />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-bold text-gray-900">{benefit}</h3>
                    <p className="text-gray-500 text-sm">Get the best value from your training investment with our comprehensive support system.</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trainer Profile */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-4xl font-bold text-gray-900">
                  Learn from the
                  <span className="text-teal-600"> Expert</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Train directly under Nirmal Narayan, an industry veteran with 25+ years of experience 
                  and authorized trainer for Rupes, Italy. His expertise in the Bigfoot polishing system 
                  and business mentorship has helped create successful entrepreneurs across India.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">Trainer Credentials</h3>
                <div className="space-y-3">
                  {mockData.founder.specializations.map((spec, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Award className="text-teal-600 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Link
                to="/about"
                className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-semibold transition-colors"
              >
                <span>Learn More About Nirmal</span>
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="relative">
              <div className="bg-slate-50 rounded-3xl p-8 border border-gray-200">
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-32 h-32 bg-teal-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                      <span className="text-4xl font-bold text-white">N</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{mockData.founder.name}</h3>
                    <p className="text-teal-600 font-semibold">Master Trainer & Industry Expert</p>
                  </div>

                  <div className="grid grid-cols-2 gap-6 pt-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-teal-600">{mockData.founder.experience}</div>
                      <div className="text-gray-500 text-sm">Experience</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-teal-600">{mockData.founder.studentsTrained}</div>
                      <div className="text-gray-500 text-sm">Students Trained</div>
                    </div>
                  </div>

                  <div className="text-center pt-4 border-t border-gray-200">
                    <p className="text-gray-600 text-sm italic">
                      "Empowering the next generation of automotive professionals with practical skills and business acumen."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Booking Section */}
      <TrainingBooking />

      {/* CTA Section */}
      <section className="py-20 bg-teal-50 border-y border-teal-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl font-bold text-gray-900">
            Start Your
            <span className="text-teal-600"> Professional Journey</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Join the ranks of successful automotive professionals. Enroll in our training programs 
            and build a rewarding career in the automotive detailing industry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#booking"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Book Training Now
            </a>
            <Link
              to="/testimonials"
              className="border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-600 hover:text-white transition-all duration-200"
            >
              Student Success Stories
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Training;